import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { positionsApi } from "@/api/positions";
import { useActiveAccount } from "@/components/ActiveAccount";
import { downloadCsv } from "@/lib/csv";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
  Label,
} from "@/components/ui";

/**
 * Closed-positions history derived from the broker-pivot internal book.
 * Defaults to the active account; filters by date + symbol.
 */
export function PositionHistoryPage() {
  const { active } = useActiveAccount();
  const [filters, setFilters] = useState({
    from_ts: "",
    to_ts: "",
    symbol: "",
    limit: 200,
  });
  const list = useQuery({
    queryKey: ["positions-history", active?.id, filters],
    queryFn: () =>
      positionsApi.history({
        trading_account_id: active!.id,
        from_ts: filters.from_ts || undefined,
        to_ts: filters.to_ts || undefined,
        limit: filters.limit,
      }),
    enabled: !!active,
    refetchInterval: 60_000,
  });

  const filtered = (list.data ?? []).filter(
    (p) => !filters.symbol || p.symbol.toUpperCase().includes(filters.symbol.toUpperCase()),
  );

  const totalPnl = filtered.reduce((s, p) => s + Number(p.realized_pnl), 0);

  function update<K extends keyof typeof filters>(k: K, v: typeof filters[K]) {
    setFilters((f) => ({ ...f, [k]: v }));
  }

  if (!active) {
    return (
      <EmptyState
        title="No active account"
        description="Pick a trading account from the navbar to view its closed positions."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">Position history</h1>
        <div className="flex items-center gap-2">
          <span className="text-2xs uppercase tracking-wider text-term-mute">
            Net P&L:
          </span>
          <span className={`tnum font-mono text-sm ${totalPnl >= 0 ? "up" : "down"}`}>
            {totalPnl >= 0 ? "+" : ""}{totalPnl.toFixed(2)}
          </span>
          <Button
            variant="ghost"
            disabled={!filtered.length}
            onClick={() => {
              if (!filtered.length) return;
              downloadCsv(
                filtered as unknown as Record<string, unknown>[],
                `position-history-${new Date().toISOString().slice(0, 10)}`,
              );
            }}
          >
            Export CSV
          </Button>
        </div>
      </div>

      <Card>
        <CardBody>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
            <div>
              <Label>Symbol</Label>
              <Input
                value={filters.symbol}
                onChange={(e) => update("symbol", e.target.value.toUpperCase())}
                placeholder="EUR/USD"
              />
            </div>
            <div>
              <Label>From</Label>
              <Input
                type="datetime-local"
                value={filters.from_ts}
                onChange={(e) => update("from_ts", e.target.value)}
              />
            </div>
            <div>
              <Label>To</Label>
              <Input
                type="datetime-local"
                value={filters.to_ts}
                onChange={(e) => update("to_ts", e.target.value)}
              />
            </div>
            <div>
              <Label>Limit</Label>
              <Input
                type="number"
                value={filters.limit}
                onChange={(e) => update("limit", Number(e.target.value))}
              />
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : filtered.length === 0 ? (
            <EmptyState
              title="No closed positions"
              description="Closed positions appear here once you've completed a round-trip on the broker pivot."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Closed</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Symbol</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Side</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Qty</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Entry</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Exit</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">P&L</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Opened</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p, i) => {
                  const pnl = Number(p.realized_pnl);
                  return (
                    <tr key={`${p.symbol}-${p.closed_at}-${i}`} className="border-t border-term-border hover:bg-term-elev">
                      <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                        {new Date(p.closed_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-term-fg">{p.symbol}</td>
                      <td className="px-3 py-1.5">
                        <Badge tone={p.side === "BUY" ? "good" : "warn"}>{p.side}</Badge>
                      </td>
                      <td className="px-3 py-1.5 text-right tnum text-term-fg">{p.qty}</td>
                      <td className="px-3 py-1.5 text-right tnum text-term-dim">{p.avg_entry}</td>
                      <td className="px-3 py-1.5 text-right tnum text-term-dim">{p.avg_exit}</td>
                      <td className={`px-3 py-1.5 text-right tnum ${pnl >= 0 ? "up" : "down"}`}>
                        {pnl >= 0 ? "+" : ""}{p.realized_pnl}
                      </td>
                      <td className="px-3 py-1.5 tnum text-term-dim whitespace-nowrap">
                        {new Date(p.opened_at).toLocaleString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
